
// frontend/src/components/EnhancedInsuranceIcons/PetInsuranceIcon.jsx
import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import styles from './EnhancedInsuranceIcons.module.css';

const PetInsuranceIcon = ({ size = 120 }) => {
  const petRef = useRef(null);
  
  useEffect(() => {
    if (!petRef.current) return;
    
    const body = petRef.current.querySelector('#pet-body');
    const head = petRef.current.querySelector('#pet-head');
    const ears = petRef.current.querySelector('#pet-ears');
    const face = petRef.current.querySelector('#pet-face');
    const legs = petRef.current.querySelector('#pet-legs');
    const tail = petRef.current.querySelector('#pet-tail');
    const collar = petRef.current.querySelector('#pet-collar');
    const tag = petRef.current.querySelector('#pet-tag');
    const bowl = petRef.current.querySelector('#pet-bowl');
    
    // Hide all elements initially
    gsap.set([body, head, ears, face, legs, tail, collar, tag, bowl], { 
      opacity: 0,
      y: -20
    });
    
    // Create animation timeline
    const tl = gsap.timeline({ defaults: { ease: "back.out(1.7)" } });
    
    // Animate each part dropping in with slight bounce effect
    tl.to(body, { opacity: 1, y: 0, duration: 0.6, delay: 0.2 })
      .to(legs, { opacity: 1, y: 0, duration: 0.5 }, "-=0.3")
      .to(head, { opacity: 1, y: 0, duration: 0.5 }, "-=0.3")
      .to(ears, { opacity: 1, y: 0, duration: 0.4 }, "-=0.2")
      .to(face, { opacity: 1, y: 0, duration: 0.4 }, "-=0.2")
      .to(tail, { opacity: 1, y: 0, duration: 0.4, rotation: -15 }, "-=0.2")
      .to(collar, { opacity: 1, y: 0, duration: 0.4 }, "-=0.2")
      .to(tag, { opacity: 1, y: 0, duration: 0.3 }, "-=0.2")
      .to(bowl, { opacity: 1, y: 0, duration: 0.4 }, "-=0.1");
    
    // Add continuous animations
    
    // Tail wagging
    gsap.to(tail, {
      rotation: 15,
      duration: 1,
      repeat: -1,
      yoyo: true,
      ease: "sine.inOut"
    });
    
    // Subtle body movement
    gsap.to(body, {
      y: "-=2",
      duration: 1.5,
      repeat: -1,
      yoyo: true,
      ease: "sine.inOut"
    });
    
    // Tag swinging
    gsap.to(tag, {
      rotation: 15,
      transformOrigin: "top center",
      duration: 1.2,
      repeat: -1,
      yoyo: true,
      ease: "sine.inOut"
    });
    
    return () => {
      // Clean up animations when component unmounts
      tl.kill();
      gsap.killTweensOf([body, head, ears, face, legs, tail, collar, tag, bowl]);
    };
  }, []);
  
  return (
    <div className={styles.iconContainer} ref={petRef} style={{ width: size, height: size }}>
      <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
        {/* Dog body */}
        <ellipse id="pet-body" cx="50" cy="60" rx="20" ry="15" fill="#F2AE40" filter="url(#shadow)" />
        
        {/* Legs */}
        <g id="pet-legs" fill="#F2AE40">
          <rect x="35" y="72" width="5" height="15" rx="2.5" />
          <rect x="45" y="72" width="5" height="15" rx="2.5" />
          <rect x="55" y="72" width="5" height="15" rx="2.5" />
          <rect x="65" y="72" width="5" height="15" rx="2.5" />
        </g>
        
        {/* Head */}
        <circle id="pet-head" cx="75" cy="50" r="12" fill="#F2AE40" filter="url(#shadow)" />
        
        {/* Ears */}
        <g id="pet-ears" fill="#B47818">
          <ellipse cx="70" cy="40" rx="6" ry="8" transform="rotate(-15)" />
          <ellipse cx="80" cy="40" rx="6" ry="8" transform="rotate(15)" />
        </g>
        
        {/* Face */}
        <g id="pet-face">
          <ellipse cx="80" cy="50" rx="5" ry="3" fill="#FBDFB1" />
          <circle cx="70" cy="46" r="2" fill="#333" />
          <circle cx="77" cy="46" r="2" fill="#333" />
          <circle cx="82" cy="50" r="1.5" fill="#333" />
        </g>
        
        {/* Tail */}
        <path id="pet-tail" d="M30,60 Q20,50 15,40" stroke="#F2AE40" strokeWidth="5" strokeLinecap="round" fill="none" />
        
        {/* Collar */}
        <path id="pet-collar" d="M65,55 A12,12 0 0,1 85,55" stroke="#DF1C41" strokeWidth="3" fill="none" />
        
        {/* Tag */}
        <g id="pet-tag" transform="translate(75, 55)">
          <circle cx="0" cy="5" r="3" fill="#2348ED" filter="url(#glow)" />
          <rect x="-0.5" y="0" width="1" height="5" fill="#DEE0E3" />
        </g>
        
        {/* Pet bowl */}
        <g id="pet-bowl" transform="translate(20, 80)">
          <ellipse cx="0" cy="0" rx="10" ry="3" fill="#38C793" />
          <ellipse cx="0" cy="-1" rx="8" ry="2" fill="#A0C4FF" opacity="0.7" />
        </g>
        
        {/* Filters */}
        <defs>
          <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="3" stdDeviation="2" floodOpacity="0.2" />
          </filter>
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="1" result="blur" />
            <feFlood floodColor="#2348ED" floodOpacity="0.5" />
            <feComposite in2="blur" operator="in" />
            <feComposite in="SourceGraphic" />
          </filter>
        </defs>
      </svg>
    </div>
  );
};

export default PetInsuranceIcon;